import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useApiMutation } from "@/packages/features-shared";
import { springs } from "../ui/motion";
import { InboxNotificationList } from "./InboxNotificationList";
import { InboxNotificationDetail } from "./InboxNotificationDetail";
import { useInboxKeyboard } from "./hooks/useInboxKeyboard";
import type { NotificationTab, NotificationWithStatus } from "./types";

export function InboxPage() {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });
  const [activeTab, setActiveTab] = useState<NotificationTab>("inbox");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [optimisticallyArchivedIds, setOptimisticallyArchivedIds] = useState<
    Set<string>
  >(new Set());

  const { data, isLoading } = useQuery(
    convexQuery(api.notifications.queries.index.list, { tab: activeTab }),
  );

  const markAllAsReadMutation = useApiMutation(
    api.notifications.mutations.index.markAllAsRead,
    {
      name: "mark-all-notifications-read",
      silentSuccess: true,
    },
  );

  const archiveMutation = useApiMutation(
    api.notifications.mutations.index.archive,
    {
      name: "archive-notification",
      silentSuccess: true,
    },
  );

  const unarchiveMutation = useApiMutation(
    api.notifications.mutations.index.unarchive,
    {
      name: "unarchive-notification",
      silentSuccess: true,
    },
  );

  const markAsReadMutation = useApiMutation(
    api.notifications.mutations.index.markAsRead,
    {
      name: "mark-notification-read",
      silentSuccess: true,
    },
  );

  const markAsUnreadMutation = useApiMutation(
    api.notifications.mutations.index.markAsUnread,
    {
      name: "mark-notification-unread",
      silentSuccess: true,
    },
  );

  const notifications = useMemo(
    () =>
      ((data?.notifications ?? []) as NotificationWithStatus[]).filter(
        (n) => !optimisticallyArchivedIds.has(n.id),
      ),
    [data?.notifications, optimisticallyArchivedIds],
  );
  const unreadCount = data?.unreadCount ?? 0;
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Clean up stale optimistic IDs once the server confirms the archive
  useEffect(() => {
    if (!data?.notifications) return;
    const serverIds = new Set<string>(
      data.notifications.map((n) => n.id as string),
    );
    setOptimisticallyArchivedIds((prev) => {
      const stale = [...prev].filter((id) => !serverIds.has(id));
      if (stale.length === 0) return prev; // no change
      const next = new Set(prev);
      for (const id of stale) next.delete(id);
      return next;
    });
  }, [data?.notifications]);

  // Build isReadMap for the keyboard hook
  const isReadMap = useMemo(
    () => Object.fromEntries(notifications.map((n) => [n.id, n.isRead])),
    [notifications],
  );

  const handleArchive = useCallback(
    (id: string) => {
      // 1. Optimistically remove from list
      setOptimisticallyArchivedIds((prev) => new Set([...prev, id]));

      // 2. Clear selection if this was the selected item
      if (selectedId === id) setSelectedId(null);

      // 3. Fire the actual mutation
      archiveMutation.mutate({
        notificationId: id as Id<"notifications">,
      });

      // 4. Show undo toast
      toast("Notification archived", {
        duration: 4000,
        action: {
          label: "Undo",
          onClick: () => {
            // Remove from optimistic set (item reappears)
            setOptimisticallyArchivedIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
            // Reverse the mutation
            unarchiveMutation.mutate({
              notificationId: id as Id<"notifications">,
            });
          },
        },
      });
    },
    [selectedId, archiveMutation, unarchiveMutation],
  );

  const handleUnarchive = useCallback(
    (id: string) => {
      unarchiveMutation.mutate({
        notificationId: id as Id<"notifications">,
      });
      if (selectedId === id) setSelectedId(null);
    },
    [unarchiveMutation, selectedId],
  );

  const handleOpenRelated = useCallback(
    (notificationId: string) => {
      const n = notifications.find((n) => n.id === notificationId);
      if (!n?.relatedEntityId || !n.relatedEntityType) return;
      if (n.relatedEntityType === "document") {
        navigate({
          to: "/$workspaceSlug/documents/$documentId",
          params: {
            workspaceSlug: workspaceSlug ?? "",
            documentId: n.relatedEntityId,
          },
        });
      } else {
        navigate({
          to: "/$workspaceSlug/audits/$auditId",
          params: {
            workspaceSlug: workspaceSlug ?? "",
            auditId: n.relatedEntityId,
          },
        });
      }
    },
    [notifications, navigate, workspaceSlug],
  );

  useInboxKeyboard({
    notifications,
    focusedIndex,
    setFocusedIndex,
    onSelect: (id) => setSelectedId(id || null),
    onArchive: handleArchive,
    onMarkRead: (id) =>
      markAsReadMutation.mutate({
        notificationId: id as Id<"notifications">,
      }),
    onMarkUnread: (id) =>
      markAsUnreadMutation.mutate({
        notificationId: id as Id<"notifications">,
      }),
    isReadMap,
  });

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h1 className="text-[15px] font-semibold text-primary">Inbox</h1>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllAsReadMutation.mutate({})}
            className="text-[12px] text-accent hover:text-accent/80 transition-colors"
          >
            Mark all read
          </button>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Left panel */}
        <div className="w-80 shrink-0 border-r border-border flex flex-col overflow-hidden">
          <InboxNotificationList
            notifications={notifications}
            selectedId={selectedId}
            activeTab={activeTab}
            isArchived={activeTab === "archive"}
            focusedIndex={focusedIndex}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setSelectedId(null);
              setFocusedIndex(null);
              setOptimisticallyArchivedIds(new Set());
            }}
            onSelect={setSelectedId}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            onMarkRead={(id) =>
              markAsReadMutation.mutate({
                notificationId: id as Id<"notifications">,
              })
            }
            onMarkUnread={(id) =>
              markAsUnreadMutation.mutate({
                notificationId: id as Id<"notifications">,
              })
            }
            onOpenRelated={handleOpenRelated}
            isLoading={isLoading}
          />
        </div>

        {/* Right panel */}
        {selectedId ? (
          <InboxNotificationDetail
            notificationId={selectedId}
            isArchived={activeTab === "archive"}
            onArchive={() => handleArchive(selectedId)}
            onUnarchive={() => handleUnarchive(selectedId)}
          />
        ) : (
          <motion.div
            className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={springs.gentle}
          >
            <div className="w-12 h-12 rounded-full bg-accent-soft flex items-center justify-center">
              <Bell size={20} className="text-accent" />
            </div>
            <p className="text-[14px] font-medium text-primary">
              Select a notification
            </p>
            <p className="text-[12px] text-secondary">
              Choose an item from the list to view details
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
